/**
 * Can this person open a page? The sidebar's own answer: a page is open when an
 * entry the sidebar drew for them covers it. A page an area owns but nothing
 * drawn covers is closed. A page no area owns (My Profile, a record's own page)
 * is not the sidebar's to close; the server still guards its data.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { menuOfDrawnAddresses, resolveDrawnEntry, resolveNavEntry, type NavMenu } from '../layout/Sidebar';
import { readDrawnNav, subscribeDrawnNav, type DrawnNav } from './drawnNav';

/** True, false, or undefined while the sidebar has not drawn yet. */
export function canOpenWith(nav: DrawnNav | null, to: string): boolean | undefined {
  // A #fragment is a place on the page, not another page.
  const hash = to.indexOf('#');
  const path = hash < 0 ? to : to.slice(0, hash);
  const at = path.indexOf('?');
  const pathname = at < 0 ? path : path.slice(0, at);
  const search = at < 0 ? '' : path.slice(at);
  const owned = resolveNavEntry(pathname, search) !== null;
  if (!nav) return owned ? undefined : true;
  const menu = nav instanceof Set ? menuOfDrawnAddresses(nav) : (nav as NavMenu);
  if (resolveDrawnEntry(menu, pathname, search)) return true;
  return !owned;
}

export function useCanOpen(): (to: string) => boolean | undefined {
  const nav = useSyncExternalStore(subscribeDrawnNav, readDrawnNav, () => null);
  return useCallback((to: string) => canOpenWith(nav, to), [nav]);
}
